import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Row, Col, Form, Table, InputGroup, Card, Button } from 'react-bootstrap';
import '../../assets/styles/ExtraList.css';
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import { OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import { upperCase } from 'lodash-es';
import { EXTRA_OWNERS, buildExtraOwnershipIndex, normalizeCatalogList, updateServiceExtras } from './extraOwnership';
// ✅ 常驻两栏版本：左侧 Catalog 勾选添加，右侧 Selected 可编辑
// ✅ Formik 是唯一真相源：不再 useEffect 回写、不再 initialSelected、不再 isEqual
// ✅ catalog 做索引：Map + 分类 ids，勾选判断用 Set(O(1))
// ✅ 主表展示名称/描述从 catalog 映射（可选：description 是否允许“商店自定义”见下方注释）


const ORDER = { bike_equipment: 1, helmet: 2, accessories: 3, insurance: 99 };

// 兼容你当前表单结构：如果你还在存 pricePerDay/maxPrice/name/description 也能跑
// 推荐后续把 formik.values.extras 收敛为：{ extraId, priceType, pricePerDay, maxPrice, isRequired, description }
const normalizeSelectedRow = (row) => ({
    id: row?.id,
    extraId: String(row?.extraId ?? ''),
    priceType: row?.priceType ?? 'PER_DAY',
    pricePerDay: row?.pricePerDay ?? '',
    maxPrice: row?.maxPrice ?? '',
    isRequired: !!row?.isRequired,
    description: row?.description ?? '',
});

const prettyType = (t) => upperCase(String(t || '').replaceAll('_', ' '));

const ExtrasList = ({ formik, extrasCatalog }) => {
    // ====== Catalog index（一次构建）======
    const fullCatalogList = useMemo(
        () => normalizeCatalogList(extrasCatalog),
        [extrasCatalog]
    );

    const ownershipIndex = useMemo(
        () => buildExtraOwnershipIndex(fullCatalogList),
        [fullCatalogList]
    );
    const baseList = ownershipIndex.visibleCatalog;
    const [selectedSnapshotByCat, setSelectedSnapshotByCat] = useState({});
    const osRef = useRef(null);
    const catalogIndex = useMemo(() => {
        const catalogById = new Map();
        const idsByType = {};

        for (const item of baseList) {
            if (!item?.id) continue;
            const id = String(item.id);
            const type = String(item?.type || 'Uncategorized');

            catalogById.set(id, item);
            (idsByType[type] ||= []).push(id);
        }

        const orderedTypes = Object.keys(idsByType).sort(
            (a, b) => (ORDER[a] ?? 50) - (ORDER[b] ?? 50) || a.localeCompare(b)
        );

        // 每个分类内部按 name 排序（稳定）
        for (const type of orderedTypes) {
            idsByType[type].sort((a, b) => {
                const A = catalogById.get(a);
                const B = catalogById.get(b);
                return String(A?.name || '').localeCompare(String(B?.name || ''));
            });
        }

        const typeById = new Map();
        const rankById = new Map();
        for (const [type, ids] of Object.entries(idsByType)) {
            ids.forEach((id, rank) => {
                typeById.set(id, type);
                rankById.set(id, rank);
            });
        }
        return { catalogById, idsByType, orderedTypes, typeById, rankById };
    }, [baseList]);

    const selected = useMemo(() => {
        const rows = Array.isArray(formik.values.extras) ? formik.values.extras : [];
        return rows
            .filter(row => row?.extraId && ownershipIndex.getOwner(row) === EXTRA_OWNERS.SERVICES)
            .map(normalizeSelectedRow);
    }, [formik.values.extras, ownershipIndex]);

    const { setValues } = formik;
    const dispatchServiceAction = useCallback(action => {
        setValues(previous => {
            const next = updateServiceExtras(previous.extras, ownershipIndex, action);
            return next === previous.extras ? previous : { ...previous, extras: next };
        }, false);
    }, [setValues, ownershipIndex]);

    const selectedIds = useMemo(
        () => new Set(selected.map((item) => item.extraId)),
        [selected]
    );

    // ====== UI state（左栏）======
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [showOnlySelected, setShowOnlySelected] = useState(false);

    useEffect(() => {
        const first = catalogIndex.orderedTypes[0] ?? null;
        setExpandedCategory(prev => {
            if (prev && catalogIndex.orderedTypes.includes(prev)) return prev;
            return first;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogIndex.orderedTypes.join('|')]);

    const toggleCategory = (category) => {
        setExpandedCategory(prev => {
            const next = prev === category ? null : category;

            if (prev === category) {
                // 收起：清掉快照（可选）
                setSelectedSnapshotByCat(s => {
                    const n = { ...s };
                    delete n[category];
                    return n;
                });
                return next;
            }

            // 展开：记录“展开时已选”的集合
            const ids = filteredIdsByType[category] || [];
            const snapshot = new Set(ids.filter(id => selectedIds.has(id)));
            setSelectedSnapshotByCat(s => ({ ...s, [category]: snapshot }));

            return next;
        });
    };


    // ====== 主表折叠（右栏）======
    const groupedSelected = useMemo(() => {
        const { typeById, rankById } = catalogIndex;

        const groups = {};
        for (const row of selected) {
            const type = typeById.get(row.extraId) || 'Uncategorized';
            (groups[type] ||= []).push(row);
        }

        // 组内按照 catalog 顺序排列（没有则按名称）
        for (const [type, rows] of Object.entries(groups)) {
            rows.sort((a, b) => {
                const ia = rankById.get(a.extraId) ?? -1;
                const ib = rankById.get(b.extraId) ?? -1;
                if (ia !== -1 || ib !== -1) return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
                const an = catalogIndex.catalogById.get(a.extraId)?.name || a.extraId;
                const bn = catalogIndex.catalogById.get(b.extraId)?.name || b.extraId;
                return String(an).localeCompare(String(bn));
            });
        }

        // 只返回有内容的分类，并按 ORDER 排序
        const entries = Object.entries(groups).filter(([, rows]) => rows?.length);
        entries.sort(([a], [b]) => (ORDER[a] ?? 50) - (ORDER[b] ?? 50) || a.localeCompare(b));
        return Object.fromEntries(entries);
    }, [catalogIndex, selected]);

    const [activeKeys, setActiveKeys] = useState([]);

    // 初始化右栏折叠：默认展开有内容的分类
    useEffect(() => {
        const keys = Object.keys(groupedSelected || {});
        setActiveKeys(keys);
    }, [Object.keys(groupedSelected || {}).join('|')]);

    // ====== Actions：增删 & 编辑（只改 formik，不做本地回写）======
    const toggleExtra = useCallback((catalogItem, checked) => {
        dispatchServiceAction({
            type: checked ? 'add' : 'remove',
            extraId: catalogItem?.id,
        });
    }, [dispatchServiceAction]);

    const removeExtra = useCallback(extraId => {
        dispatchServiceAction({ type: 'remove', extraId });
    }, [dispatchServiceAction]);

    const patchRow = useCallback((extraId, patch) => {
        dispatchServiceAction({ type: 'patch', extraId, patch });
    }, [dispatchServiceAction]);

    // ====== 左栏：过滤后的 catalog 数据（轻量）======
    const lowerQuery = searchQuery.trim().toLowerCase();

    const filteredIdsByType = useMemo(() => {
        const { idsByType, catalogById } = catalogIndex;

        const out = {};
        for (const type of catalogIndex.orderedTypes) {
            const ids = idsByType[type] || [];
            let list = ids;

            if (showOnlySelected) {
                list = list.filter(id => selectedIds.has(id));
            }

            if (lowerQuery) {
                const typeMatch = String(type).toLowerCase().includes(lowerQuery);
                if (!typeMatch) {
                    list = list.filter(id => {
                        const item = catalogById.get(id);
                        const name = String(item?.name || '').toLowerCase();
                        const desc = String(item?.description || '').toLowerCase();
                        return name.includes(lowerQuery) || desc.includes(lowerQuery);
                    });
                }
            }

            if (list.length) out[type] = list;
        }
        return out;
    }, [catalogIndex, lowerQuery, selectedIds, showOnlySelected]);

    const leftTypes = useMemo(() => Object.keys(filteredIdsByType), [filteredIdsByType]);
    const flatSearchResults = useMemo(() => {
        if (!lowerQuery) return [];

        const rows = [];
        for (const [type, ids] of Object.entries(filteredIdsByType)) {
            for (const id of ids) {
                const item = catalogIndex.catalogById.get(id);
                if (!item) continue;
                rows.push({ id, type, item });
            }
        }

        // 已选优先 + name
        rows.sort((a, b) => {
            const aSel = selectedIds.has(a.id);
            const bSel = selectedIds.has(b.id);
            if (aSel !== bSel) return bSel - aSel;
            return String(a.item?.name || '').localeCompare(String(b.item?.name || ''));
        });

        return rows;
    }, [lowerQuery, filteredIdsByType, catalogIndex.catalogById, selectedIds]);

    // ====== Render ======
    const rightEntries = Object.entries(groupedSelected || {});

    useEffect(() => {
        const inst = osRef.current?.osInstance?.();
        if (!inst) return;

        const viewport = inst.elements().viewport; // ✅ 真正滚动的元素
        if (!viewport) return;

        // 给 viewport 加 class（用于 cursor / 选中文字禁用）
        viewport.classList.add('drag-scroll-viewport');

        const state = {
            isDown: false,
            startY: 0,
            startScrollTop: 0,
            moved: false,
        };

        const onMouseDown = (e) => {
            if (e.button !== 0) return;

            // 点在可交互控件上就别启用拖拽，避免影响勾选/输入
            if (e.target.closest('input, label, button, a, select, textarea')) return;

            state.isDown = true;
            state.moved = false;
            state.startY = e.clientY;
            state.startScrollTop = viewport.scrollTop;

            viewport.classList.add('drag-scrolling');
        };

        const onMouseMove = (e) => {
            if (!state.isDown) return;
            const dy = e.clientY - state.startY;
            if (Math.abs(dy) > 3) state.moved = true;
            viewport.scrollTop = state.startScrollTop - dy;
        };

        const onMouseUp = () => {
            if (!state.isDown) return;
            state.isDown = false;
            viewport.classList.remove('drag-scrolling');
        };

        viewport.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        return () => {
            viewport.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);

            viewport.classList.remove('drag-scroll-viewport');
            viewport.classList.remove('drag-scrolling');
        };
    }, []);

    return (
        <Row className="extras-two-col">
            {/* 左：Catalog（常驻） */}
            <Col md={4} lg={4} xl={3} className="position-relative">
                <div className="extras-sidebar" style={{ position: 'sticky', top: 12 }}>
                    <div className="sidebar-header d-flex align-items-center justify-content-between">
                        <span>Extras Catalog</span>
                    </div>

                    <div className="my-3">
                        <Form.Control
                            type="text"
                            className="form-control"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search extras..."
                        />
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <Form.Check
                            type="switch"
                            id="only-selected"
                            label="Only selected"
                            checked={showOnlySelected}
                            onChange={(e) => setShowOnlySelected(e.target.checked)}
                        />
                        <small className="text-muted">
                            {selected.length} selected
                        </small>
                    </div>

                    <OverlayScrollbarsComponent
                        ref={osRef}
                        options={{
                            scrollbars: { autoHide: "leave", autoHideDelay: 0, theme: "os-theme-custom" },
                            overflow: { x: "hidden", y: "scroll" }
                        }}
                        className="sidebar-content mt-2"
                        style={{ maxHeight: '72vh' }}
                    >
                        {lowerQuery ? (
                            // ===== 搜索模式：扁平 Search Results（复用普通行样式）=====
                            flatSearchResults.length === 0 ? (
                                <Card className="p-3 text-center text-muted">
                                    <small>No results.</small>
                                </Card>
                            ) : (
                                <div className="search-results">
                                    <div className="search-results__meta">
                                        <small className="text-muted">{flatSearchResults.length} results</small>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setSearchQuery('')}>
                                            Clear
                                        </Button>
                                    </div>

                                    {/* ✅ 关键：复用 items-list + selected-item */}
                                    <div className="items-list">
                                        {flatSearchResults.map(({ id, type, item }) => {
                                            const checked = selectedIds.has(id);

                                            return (
                                                <div key={id} className="selected-item py-1">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`extra-${id}`}
                                                        label={
                                                            <div className="d-flex flex-column">
                                                                <span>{item?.name}</span>

                                                                {item?.description && (
                                                                    <OverlayTrigger
                                                                        placement="right"
                                                                        overlay={<Tooltip id={`tooltip-${id}`}>{item.description}</Tooltip>}
                                                                    >
                                                                        <small className="text-muted extra-desc">{item.description}</small>
                                                                    </OverlayTrigger>
                                                                )}
                                                            </div>
                                                        }
                                                        checked={checked}
                                                        onChange={(e) => toggleExtra(item, e.target.checked)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )
                        ) : (
                            // ===== 普通模式：分类 Accordion 浏览 =====
                            leftTypes.length === 0 ? (
                                <Card className="p-3 text-center text-muted">
                                    <small>No extras found.</small>
                                </Card>
                            ) : (
                                leftTypes.map((category) => {
                                    const ids = filteredIdsByType[category] || [];
                                    const selectedInCat = ids.filter(id => selectedIds.has(id));
                                    const othersInCat = ids.filter(id => !selectedIds.has(id));
                                    const totalInCat = catalogIndex.idsByType[category]?.length ?? ids.length;
                                    const checkedCount = (catalogIndex.idsByType[category] || []).filter(id => selectedIds.has(id)).length;

                                    const shouldExpand = expandedCategory === category;

                                    return (
                                        <div key={category} className={`category-card mb-2 p-0 ${shouldExpand ? 'is-open' : ''}`}>
                                            <div
                                                className="category-header"
                                                onClick={() => toggleCategory(category)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span className='me-2'>{shouldExpand ? '−' : '+'}</span>
                                                <span>
                                                    {prettyType(category)}
                                                    <span className="text-muted ms-2">({checkedCount}/{totalInCat})</span>
                                                </span>
                                            </div>

                                            {shouldExpand && (() => {
                                                const ids = filteredIdsByType[category] || [];
                                                const snap = selectedSnapshotByCat[category] || new Set();

                                                // ✅ “展开时已选”的排在前面（稳定，不会因为新勾选跳动）
                                                const stableOrderedIds = [
                                                    ...ids.filter(id => snap.has(id)),
                                                    ...ids.filter(id => !snap.has(id)),
                                                ];

                                                return (
                                                    <div className="items-list">
                                                        {stableOrderedIds.map((id, idx) => {
                                                            const item = catalogIndex.catalogById.get(id);
                                                            if (!item) return null;

                                                            const checked = selectedIds.has(id);

                                                            // ✅ 可选：在“分界点”处插入一个很轻的分隔（不显示文字）
                                                            const boundaryIndex = ids.filter(x => snap.has(x)).length;
                                                            const showDivider = boundaryIndex > 0 && idx === boundaryIndex;

                                                            return (
                                                                <React.Fragment key={id}>
                                                                    {showDivider && <div className="items-divider" />}

                                                                    <div className="selected-item py-1">
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            id={`extra-${id}`}
                                                                            label={
                                                                                <div className="d-flex flex-column">
                                                                                    <span>{item?.name}</span>

                                                                                    {item?.description && (
                                                                                        <OverlayTrigger
                                                                                            placement="right"
                                                                                            overlay={<Tooltip id={`tooltip-${id}`}>{item.description}</Tooltip>}
                                                                                        >
                                                                                            <small className="text-muted extra-desc">{item.description}</small>
                                                                                        </OverlayTrigger>
                                                                                    )}
                                                                                </div>
                                                                            }
                                                                            checked={checked}
                                                                            onChange={(e) => toggleExtra(item, e.target.checked)}
                                                                        />
                                                                    </div>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}


                                        </div>
                                    );
                                })
                            )
                        )}

                    </OverlayScrollbarsComponent>
                </div>
            </Col>

            {/* 右：Selected（可编辑） */}
            <Col md={8} lg={8} xl={9} className="position-relative" style={{ minHeight: '80vh' }}>

                <div className="available-extras fs-6">
                    <div className="selected-header">
                        <h6 className="mb-1 fw-semibold">Selected Extras</h6>
                        <small className="text-muted">
                            Configure billing, pricing and requirements
                        </small>
                    </div>
                    {rightEntries.length === 0 ? (
                        <Card className="p-3 text-center text-muted">
                            <small>No extras selected yet.</small>
                        </Card>
                    ) : (
                        <div className="category-content my-0">
                            <Table className="my-1" hover responsive>
                                <colgroup>
                                    {[
                                        <col key="name" />,
                                        <col key="price" style={{ width: '14rem' }} />,
                                        <col key="max" style={{ width: '14rem' }} />,
                                        <col key="req" style={{ width: '7rem' }} />,
                                        <col key="act" style={{ width: '12rem' }} />,
                                    ]}
                                </colgroup>

                                {/* ✅ 全局表头（只一次） */}
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th style={{ width: '14rem' }}>Price</th>
                                        <th style={{ width: '14rem' }}>Max</th>
                                        <th style={{ width: '7rem' }} className="text-center">Required</th>
                                        <th style={{ width: '12rem' }} className="text-center">Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {rightEntries.map(([category, items]) => {
                                        const isOpen = activeKeys.includes(category);

                                        return (
                                            <React.Fragment key={category}>
                                                {/* ✅ 分类头：保留你原来的 className="category-section" + "category-header" */}
                                                <tr className="category-section">
                                                    <td colSpan={5} className="p-0">
                                                        <div
                                                            className="category-header rounded d-flex align-items-center justify-content-between"
                                                            onClick={() => {
                                                                setActiveKeys(prev =>
                                                                    prev.includes(category)
                                                                        ? prev.filter(c => c !== category)
                                                                        : [...prev, category]
                                                                );
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <span className="me-2 fw-bold">{isOpen ? '−' : '+'}</span>
                                                                <span className="mb-0 fs-6">
                                                                    {upperCase(String(category)).replaceAll('_', ' ')}
                                                                </span>
                                                            </div>
                                                            <small className="text-muted">{items.length}</small>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* ✅ 明细：展开时渲染，行样式保持原来 */}
                                                {isOpen && (items || []).map((row, i) => {
                                                    const catalog = catalogIndex.catalogById.get(row.extraId);
                                                    const displayName = catalog?.name || row.extraId;
                                                    const isPerDay = row.priceType !== 'ONE_TIME';

                                                    return (
                                                        <tr key={row.extraId ?? `row-${i}`}>
                                                            <td className="extra-name-cell">
                                                                <span className="extra-name">{displayName}</span>
                                                            </td>

                                                            {/* Price */}
                                                            <td>
                                                                <InputGroup>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={row.pricePerDay ?? ''}
                                                                        onChange={(e) =>
                                                                            patchRow(row.extraId, {
                                                                                pricePerDay: e.target.value === '' ? '' : parseFloat(e.target.value),
                                                                            })
                                                                        }
                                                                    />
                                                                    <InputGroup.Text className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                                                                        {row.priceType === 'ONE_TIME' ? '€' : '€/day'}
                                                                    </InputGroup.Text>
                                                                </InputGroup>
                                                            </td>

                                                            {/* Max */}
                                                            <td>
                                                                {isPerDay ? (
                                                                    <InputGroup>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={row.maxPrice ?? ''}
                                                                            onChange={(e) =>
                                                                                patchRow(row.extraId, {
                                                                                    maxPrice: e.target.value === '' ? '' : parseFloat(e.target.value),
                                                                                })
                                                                            }
                                                                        />
                                                                        <InputGroup.Text className="text-muted">€</InputGroup.Text>
                                                                    </InputGroup>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>

                                                            {/* Required */}
                                                            <td className="text-center">
                                                                <Form.Check
                                                                    type="switch"
                                                                    id={`required-${row.extraId}`}
                                                                    checked={!!row.isRequired}
                                                                    onChange={(e) =>
                                                                        patchRow(row.extraId, { isRequired: e.target.checked })
                                                                    }
                                                                />
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="text-center">
                                                                <div className="actions-wrap">
                                                                    <OverlayTrigger
                                                                        trigger="click"
                                                                        placement="left"
                                                                        rootClose
                                                                        overlay={
                                                                            <Popover id={`adv-${row.extraId}`} style={{ minWidth: 320 }}>
                                                                                <Popover.Header as="h3">Advanced settings</Popover.Header>
                                                                                <Popover.Body>
                                                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                                                        <div>
                                                                                            <div className="fw-semibold" style={{ fontSize: 14 }}>
                                                                                                Billing mode
                                                                                            </div>
                                                                                            <div className="text-muted" style={{ fontSize: 12 }}>
                                                                                                Switch only if this extra is charged once.
                                                                                            </div>
                                                                                        </div>

                                                                                        <Form.Check
                                                                                            type="switch"
                                                                                            id={`billing-${row.extraId}`}
                                                                                            label={isPerDay ? 'Per day' : 'One-time'}
                                                                                            checked={isPerDay}
                                                                                            onChange={(e) =>
                                                                                                patchRow(row.extraId, {
                                                                                                    priceType: e.target.checked ? 'PER_DAY' : 'ONE_TIME',
                                                                                                    maxPrice: e.target.checked ? (row.maxPrice ?? 0) : 0,
                                                                                                })
                                                                                            }
                                                                                        />
                                                                                    </div>

                                                                                    <div className="fw-semibold mb-1" style={{ fontSize: 14 }}>
                                                                                        Description
                                                                                    </div>
                                                                                    <Form.Control
                                                                                        as="textarea"
                                                                                        rows={3}
                                                                                        value={row.description ?? ''}
                                                                                        onChange={(e) =>
                                                                                            patchRow(row.extraId, { description: e.target.value })
                                                                                        }
                                                                                        placeholder="Add a note for this extra..."
                                                                                    />
                                                                                </Popover.Body>
                                                                            </Popover>
                                                                        }
                                                                    >
                                                                        <Button size="sm" variant="outline-secondary">
                                                                            Settings
                                                                        </Button>
                                                                    </OverlayTrigger>

                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-danger"
                                                                        onClick={() => removeExtra(row.extraId)}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}

                </div>
            </Col>
        </Row>
    );
};

export default ExtrasList;
